import { koComponent } from '../../ko-utils';
import * as siteConfig from '../../site-config/trackableConfig';
import { beginNoTracking, endNoTracking } from '../../utils/changeTracking';
import { stripHtml } from '../../utils/stripHtml';
import { fire } from '../../utils/events';

class RulesDescriptor {
    constructor(params) {
        let segmentsClean = [];
        if (ko.unwrap(params.segmentsList)) {
            segmentsClean = ko.unwrap(params.segmentsList).map(i => {
                if (i.toJS) return i.toJS();

                return i;
            })
        }

        this.root = params.rootElement;
        this.toursList = ko.unwrap(params.toursList) || [];
        this.segmentsList = segmentsClean;

        if (!this.root()) {
            this.root.noTracking = true;
            this.root(new siteConfig.RulesDescriptor());
            this.root.noTracking = false;

            beginNoTracking(this.root());
            this.root().condOp('AND');
            endNoTracking(this.root());
        }

        this.mainRulesGroup = this.root();

        this.customVisible = ko.observable(false);

        const languages = window.auth && window.auth.project ? window.auth.project.SupportedLanguages : [];

        this.operators = [
            { value: '=', text: 'equals' },
            { value: '!=', text: 'doesn\'t equal' },
            { value: '>', text: 'is larger than' },
            { value: '<', text: 'is less than' },
            { value: '>=', text: 'is larger or equals' },
            { value: '<=', text: 'is less or equals' },
            { value: 'contains', text: 'contains' },
            { value: 'contains_not', text: 'doesn\'t contain' },
        ];

        this.predefinedProps = [
            { value: 'user.role', text: 'User\'s role', group: 'User', className: '' },
            { value: 'user.username', text: 'User\'s username', group: 'User', className: '' },
            { value: 'user.email', text: 'User\'s e-mail', group: 'User', className: '' },
            {
                value: 'user.signUpAt',
                text: 'User\'s sign up date',
                group: 'User',
                className: '',
                operators: [
                    { value: '=', text: 'equals' },
                    { value: '!=', text: 'doesn\'t equal' },
                    { value: '>', text: 'is larger than' },
                    { value: '<', text: 'is less than' },
                    { value: '>=', text: 'is larger or equals' },
                    { value: '<=', text: 'is less or equals' },
                    { value: 'contains', text: 'contains' },
                    { value: 'd_ago', text: 'is at least', inputPostfix: 'days ago' },
                    { value: 'd_ago_max', text: 'was no more than', inputPostfix: 'days ago' },
                ]
            },
            { value: 'user.firstname', text: 'User\'s first name', group: 'User', className: '' },
            { value: 'user.lastname', text: 'User\'s last name', group: 'User', className: '' },
            {
                value: 'env.language',
                text: 'Language',
                options: languages.map(i => {
                    return {
                        value: i,
                        text: i
                    };
                }),
                operators: [
                    { value: '=', text: 'is' },
                    { value: '!=', text: 'is not' }
                ],
                group: 'Environment',
                className: ''
            },
            {
                value: 'device.type',
                text: 'Device type',
                options: [
                    { value: 'mobile', text: 'Mobile' },
                    { value: 'desktop', text: 'Desktop' },
                ],
                operators: [
                    { value: '=', text: 'is' },
                    { value: '!=', text: 'is not' }
                ],
                group: 'Device',
                className: ''
            },

            //{ value: 'pfUser.created', text: 'Created date', group: 'Product Fruits user', className: '' },
            //{ value: 'pfUser.created_dago', text: 'Created days ago', group: 'Product Fruits user', className: '' },
        ];

        if (this.toursList.length > 0) {
            const tourOperators = [
                { value: 'tour_state_valid', text: 'applies for', displayText: 'is' },
                { value: 'tour_state_invalid', text: 'doesn\'t apply for', displayText: 'is not' }
            ];
            const tours = this.toursList.map(tour => ({ text: tour.name, value: tour.id }));

            Array.prototype.push.apply(this.predefinedProps, [
                { value: 'tour.fin', text: 'Tour finished', group: 'Tours', className: '', options: tours, operators: tourOperators, displayText: 'finished' },
                { value: 'tour.skip', text: 'Tour skipped', group: 'Tours', className: '', options: tours, operators: tourOperators, displayText: 'skipped' },
                { value: 'tour.fin_skip', text: 'Tour finished or skipped', group: 'Tours', className: '', options: tours, operators: tourOperators, displayText: 'finished or skipped' },
            ]);
        }

        if (this.segmentsList.length > 0) {
            const segmentOperators = [
                { value: 'segment_match', text: 'Is member of', displayText: 'matches' },
                { value: 'segment_match_not', text: 'Is not member of', displayText: 'doesn\'t match' }
            ];
            const segments = this.segmentsList.map(segment => ({ text: segment.name, value: segment.id }));

            Array.prototype.push.apply(this.predefinedProps, [
                { value: 'segment.match', text: 'Segment', group: 'Segments', className: '', options: segments, operators: segmentOperators, displayText: ' ' },
            ]);
        }

        Array.prototype.push.apply(this.predefinedProps, [
            {
                value: '', text: 'Custom property', className: 'rules-custom', operators: [
                    { value: '=', text: 'equals' },
                    { value: '!=', text: 'doesn\'t equal' },
                    { value: '>', text: 'is larger than' },
                    { value: '<', text: 'is less than' },
                    { value: '>=', text: 'is larger or equals' },
                    { value: '<=', text: 'is less or equals' },
                    { value: 'contains', text: 'contains' },
                    { value: 'contains_not', text: 'doesn\'t contain' },
                    { value: 'd_ago', text: 'is at least (date)', inputPostfix: 'days ago' },
                    { value: 'd_ago_max', text: 'was no more than (date)', inputPostfix: 'days ago' },
                ]
            },
        ]);

        this.groupedProps = () => {
            let res = [];

            for (const prop of this.predefinedProps) {
                if (prop.group) {
                    let existingGroup = res.filter(g => g.group == prop.group)[0];
                    if (!existingGroup) {
                        existingGroup = { group: prop.group, items: [] };
                        res.push(existingGroup);
                    }
                    existingGroup.items.push(prop);
                } else {
                    res.push(prop);
                }
            }

            return res;
        }

        if (this.mainRulesGroup.rules) {
            this.mainRulesGroup.rules(this.mainRulesGroup.rules().map(rule => {

                beginNoTracking(rule);
                rule.expression(this.decorateExpression(ko.unwrap(rule.expression)));
                endNoTracking(rule);

                return rule;
            }));
        }
    }

    addRule() {
        const exp = new siteConfig.RuleExpression();

        beginNoTracking(exp);
        exp.prop('user.username');
        exp.op('=');
        exp.val('');
        endNoTracking(exp);

        const rule = new siteConfig.RuleDescriptor();

        beginNoTracking(rule);
        rule.expression(this.decorateExpression(exp));
        endNoTracking(rule);

        this.mainRulesGroup.rules.push(rule);
    }

    decorateExpression(expression) {
        if (expression) {
            expression.getPropOperators = ko.computed(() => {
                const propVal = ko.unwrap(expression.prop);

                const prop = this.predefinedProps.filter(i => i.value == propVal)[0];

                if (prop && prop.operators) {
                    return prop.operators;
                    //return this.operators.filter(i => prop.operators.indexOf(i.value) !== -1);
                }

                if (propVal && propVal.startsWith && propVal.startsWith('user.props.')) {
                    return this.predefinedProps.filter(i => i.className == 'rules-custom')[0].operators; // temp fix
                }

                return this.operators;
            });

            expression.getPropValType = ko.computed(() => {
                const propVal = ko.unwrap(expression.prop);

                const prop = this.predefinedProps.filter(i => i.value == propVal)[0];

                if (prop && prop.options) {
                    return 'options';
                } else {
                    return 'text';
                }
            });

            expression.getPropOptions = ko.computed(() => {
                const propVal = ko.unwrap(expression.prop);

                const prop = this.predefinedProps.filter(i => i.value == propVal)[0];

                if (!prop) return [];

                return prop.options;
            });

            expression.isCustomRule = ko.computed(() => {
                const v = ko.unwrap(expression.prop);
                //console.log('ISCUS', v, !this.predefinedProps.some(i => i.value === v && v !== ''));
                return !this.predefinedProps.some(i => i.value === v && v !== '');
            });

            expression.getInputPostfix = ko.computed(() => {
                const propVal = ko.unwrap(expression.prop);
                const propOp = ko.unwrap(expression.op);

                let prop = this.predefinedProps.filter(i => i.value == propVal)[0];
                if (!prop && propVal && propVal.startsWith) {
                    prop = this.predefinedProps.filter(i => i.className == 'rules-custom')[0]
                }

                if (prop && prop.operators) {
                    const op = prop.operators.filter(o => o.value == propOp)[0];

                    return op ? op.inputPostfix : '';
                    //return this.operators.filter(i => prop.operators.indexOf(i.value) !== -1);
                }

                return '';
            });
        }

        return expression;
    }

    ruleSummary(rule) {
        const exp = ko.unwrap(rule.expression);

        const op = ko.unwrap(exp.op);
        const val = ko.unwrap(exp.val);
        const prop = ko.unwrap(exp.prop) || '';

        let propName = prop;
        let opName = op;
        let opInputPostFix = '';

        const genOperator = this.operators.filter(o => o.value == op)[0];
        if (genOperator) {
            opName = genOperator.text;
        }

        let isCustomProp = false;
        let predefinedProp = this.predefinedProps.filter(i => i.value == prop)[0];
        if (propName && propName.startsWith && propName.startsWith('user.props.')) {
            isCustomProp = true;
            predefinedProp = this.predefinedProps.filter(i => i.className == 'rules-custom')[0];
        }
        if (predefinedProp) {
            if (!isCustomProp) {
                propName = predefinedProp.displayText || predefinedProp.text;
            }

            if (predefinedProp.operators) {
                const pOp = predefinedProp.operators.filter(i => i.value == op)[0];

                if (pOp) {
                    opName = pOp.displayText || pOp.text;
                    opInputPostFix = pOp.inputPostfix || '';
                }
            }
        }

        let valName = stripHtml(val);
        //console.log('AA', valName, prop);
        if (prop.startsWith('tour.')) {
            const tour = this.toursList.filter(t => t.id == val)[0];

            if (tour) {
                valName = tour.name;

                return `<b>${valName}</b> ${opName} ${propName}`;
            }
        }

        if (prop.startsWith('segment.')) {
            const segment = this.segmentsList.filter(t => t.id == val)[0];

            if (segment) {
                valName = segment.name;

                return `Segment <b>${valName}</b> ${opName} ${propName}`;
            }
        }

        return `${propName} ${opName} ${formatVal(valName)} ${opInputPostFix}`;

        function formatVal(v) {
            if (v === '' || typeof v === 'undefined' || v === null) {
                return `<i>empty</i>`
            } else {
                return `<b>${v}</b>`
            }
        }
    }

    removeRule(rule) {
        this.mainRulesGroup.rules.remove(rule);
    }

    closeDropdown() {
        fire('general-dropdown-close');
    }
}

export default koComponent('rules-descriptor', RulesDescriptor);

export function areRulesDefined(rulesObject) {
    const obj = ko.unwrap(rulesObject);
    return obj && obj.rules && ko.unwrap(obj.rules).length > 0;
}